// Copyright (c) Docugami, Inc. All rights reserved.
import React, { ReactNode, useMemo, useEffect, useState } from 'react';
import { primaryTheme, homeGray, clauseSelectedGreen, greyText, backgroundGray } from '../shell/presentation/AppStyles';
import { useDgIcons, dgIconNames } from '../../hooks/dgIcons';
import styled from 'styled-components';
import { DgSingleLineEllipsis, DgMultineEllipsis, DgNonSelectableLabelCss, DgFlexCenteredRow, DisabledCss } from '../common/DgCommonStyledComponents';
import { dgFontFamily, dgTagFontFamily, userFontFamily } from 'components/common/Fonts';
import { decodeLabel } from 'utils/TagStandardUtils';
import { CommandButton, ICommandButton, IconAlignment } from 'components/cards/CommandButton';
import { DgIconButton } from 'components/shell/utils/DgIconButton';
import { DirectionalHint, TooltipHost } from '@fluentui/react';
import { toPascalCase } from 'utils/StringUtils';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 3px;
    margin: 5px;
    background: ${primaryTheme.palette.white};
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.15);
    border-radius: 5px;
    box-sizing: border-box;
`;

const CardletInfoHeaderContainer = styled(DgFlexCenteredRow)`
    padding: 5px;
    box-sizing: border-box;
    justify-content: center;
`;

const Divider = styled.div`
    width: 100%;
    border-top: 0.5px solid ${backgroundGray};
`;

export interface ICardletItem {
    text: string;
    selected: boolean;
    select: () => void;
    commands?: ICommandButton[];
    infoText?: string | JSX.Element | JSX.Element[];
}

export interface CardletProps {
    id?: string;
    label: string;
    onExpand?: () => void;
    items: ICardletItem[];
    isSelected: boolean;
    onSelect: () => void;
    commands: ICommandButton[];
    selectedColor?: string;
    infoHeader?: ReactNode;
}

export const Cardlet = (props: CardletProps): JSX.Element => {
    return (
        <Container id={props.id} onClick={props.onSelect}>
            {!!props.infoHeader && <CardletInfoHeaderContainer>{props.infoHeader}</CardletInfoHeaderContainer>}
            <Divider />
            <CardletLabelHeader label={decodeLabel(props.label)} onExpand={props.isSelected ? props.onExpand : undefined} />
            <Divider />
            <CardletItem items={props.items} isCardletSelected={props.isSelected} selectedColor={props.selectedColor} />
            <Divider />
            <CardletFooter commands={props.commands} />
        </Container>
    )
}

const CardletItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const CardletItemText = styled(DgMultineEllipsis) <{ selected?: boolean, selectedColor?: string }>`
    width: 100%;
    font-family: ${userFontFamily};
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 15px;
    color: ${homeGray};
    cursor: pointer;
    background-color: ${props => !!props.selectedColor ? `${props.selectedColor}80` : !!props.selected ? `${clauseSelectedGreen}80` : ''};
`;

const VariantItemsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    align-items: center;
    width: 100%;
`;

const CommandsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 5px 0px;
`;

const ItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 90%;
    padding: 5px;
`;

const InfoText = styled.span`
    font-family: ${dgTagFontFamily};
    font-size: 10px;
    font-weight: 400;
    line-height: 11.5px;
    text-align: center;
    margin-top: 8px;
    color: ${greyText};
`;

interface CardletItemProps {
    items: ICardletItem[];
    isCardletSelected: boolean;
    selectedColor?: string;
}

const CardletItem = (props: CardletItemProps) => {
    const [variantsOpen, setVariantsOpen] = useState(false);

    useEffect(() => {
        // If the cardlet is deselected, hide the variants dropdown
        if (!props.isCardletSelected) {
            setVariantsOpen(false);
        }
    }, [props.isCardletSelected])

    const displayItem = useMemo(() => !props.items.length ? null : props.items[0], [props.items]);
    const variantItems = useMemo(() => !props.items.length ? [] : props.items.slice(1), [props.items]);

    useEffect(() => {
        // If a variant in the dropdown list gets selected, and the dropdown was closed,
        // open it to show the selected item
        if (!!variantItems.find(v => !!v.selected) && !variantsOpen) {
            setVariantsOpen(true);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [variantItems])

    useEffect(() => {
        // If the variants dropdown is closed, and a variant was selected,
        // select the display item instead
        if (!!variantItems.find(v => !!v.selected) && !variantsOpen) {
            displayItem!.select();
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [variantsOpen])

    function getItemText(item: ICardletItem, index?: number) {
        return (
            <ItemContainer key={`item_${index}`}>
                {!!item.selected && !!item.commands && (
                    <CommandsContainer key={`commands_container`}>
                        {
                            item.commands.map(c => (
                                <CommandButton
                                    dataAutomationId={toPascalCase(c.label, '')}
                                    key={c.label}
                                    command={c}
                                    width={c.icon.width}
                                    height={c.icon.height}
                                    fontSize={8}
                                    iconAlignment={IconAlignment.Left} />
                            ))
                        }
                    </CommandsContainer>
                )}
                <CardletItemText
                    key={`cardlet_text`}
                    onClick={item.select}
                    numLines={item.selected ? 5 : 3}
                    selectedColor={item.selected ? props.selectedColor : ''}>
                    {item.text}
                </CardletItemText>
                {!!item.selected && !!item.infoText && <InfoText key={`info_text`}>{item.infoText}</InfoText>}
            </ItemContainer>
        )
    }

    if (!props.items.length) {
        return null;
    }

    return (
        <CardletItemContainer>
            {getItemText(displayItem!, 0)}
            {!!variantItems.length && (
                <>
                    <CardletDropdownLabel
                        fontSize={8}
                        label={variantsOpen ? "HIDE VARIATIONS" : "SHOW VARIATIONS"}
                        dropdownOpen={variantsOpen}
                        onClick={() => setVariantsOpen(v => !v)} />
                    {!!props.isCardletSelected && !!variantsOpen && (
                        <VariantItemsContainer key={'variant_items '}>
                            {
                                variantItems.map((v, idx) => (
                                    <React.Fragment key={`variant_${idx}`}>
                                        <Divider key={`divider_${idx}`} />
                                        {getItemText(v, idx)}
                                    </React.Fragment>
                                ))
                            }
                        </VariantItemsContainer>
                    )}
                </>
            )}
        </CardletItemContainer>
    )
}


const CardletLabelHeaderContainer = styled(DgFlexCenteredRow)`
    width: 100%;
    padding: 5px;
    box-sizing: border-box;
    overflow: hidden;
`;

const CardletLabelText = styled(DgSingleLineEllipsis)`
    font-family: ${dgTagFontFamily};
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 21px;
    color: ${primaryTheme.palette.black};
    ${DgNonSelectableLabelCss}
`;

const AAExpandContainer = styled(DgFlexCenteredRow)`
    background-color: ${backgroundGray};
    justify-content: flex-end;
    height: 20px;
    padding: 0 5px;
    cursor: pointer;
    width: auto;
    margin-left: auto;
    border-radius: 5px;
    box-sizing: border-box;
`;

const AaCardHeaderExpandLabel = styled.span`
    font-family: ${dgTagFontFamily};
    font-style: normal;
    font-weight: bold;
    font-size: 8px;
    line-height: 9px;
    text-align: center;
    ${DgNonSelectableLabelCss}
    color: ${greyText};

    &:hover {
        cursor: pointer;
    }
`;

const ExpandButtonContainer = styled.div`
    margin-top: -5px;
    padding-right: 5px;
`;

interface CardletLabelHeaderProps {
    label: string;
    onExpand?: () => void;
}

const CardletLabelHeader = (props: CardletLabelHeaderProps): JSX.Element => {
    return (
        <CardletLabelHeaderContainer>
            <CardletLabelText>{props.label}</CardletLabelText>
            {!!props.onExpand && (
                <AAExpandContainer onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onExpand?.();
                }}>
                    <ExpandButtonContainer>
                        <DgIconButton
                            iconName={dgIconNames.ExpandCard}
                            height={8}
                            width={8}
                            color={greyText}
                            shadow={''}
                            onClick={props.onExpand} />
                    </ExpandButtonContainer>
                    <AaCardHeaderExpandLabel>Expand</AaCardHeaderExpandLabel>
                </AAExpandContainer>
            )}
        </CardletLabelHeaderContainer>
    );
}


const CardletFooterContainer = styled(DgFlexCenteredRow)`
    padding: 5px;
    box-sizing: border-box;
    justify-content: space-between;
`;

interface CardletFooterProps {
    commands: ICommandButton[];
}

const ButtonContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;

    :last-of-type {
        justify-content: right;
    }
    :first-of-type {
        justify-content: left;
    }
`;

const CardletFooter = (props: CardletFooterProps): JSX.Element => {
    return (
        <CardletFooterContainer>
            {
                props.commands.map(c => (
                    <ButtonContainer key={c.label}>
                        <CommandButton
                            dataAutomationId={toPascalCase(c.label, '')}
                            command={c}
                            width={c.icon.width}
                            height={c.icon.height}
                            iconAlignment={IconAlignment.Left} />
                    </ButtonContainer>
                ))
            }
        </CardletFooterContainer>
    );
}


const MarkerContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2px;
    font-size: 8px;
    align-items: center;

    i {
        font-size: unset;
    }
`;

const MarkerText = styled.div`
    font-family: ${dgFontFamily};
    color: ${homeGray};
`;

interface CardletHeaderMarkerContentProps {
    label: string;
    icon?: JSX.Element;
    tooltip?: string;
}

export const CardletHeaderMarkerContent = (props: CardletHeaderMarkerContentProps): JSX.Element => {
    return (
        <TooltipHost content={props.tooltip} directionalHint={DirectionalHint.topCenter}>
            <MarkerContainer>
                {props.icon}
                <MarkerText>{props.label}</MarkerText>
            </MarkerContainer>
        </TooltipHost>
    )
}


const DropdownButtonContainer = styled.div<{ disabled?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: center;
    gap: 5px;
    cursor: pointer;
    margin: 5px 0px;
    ${props => !!props.disabled && DisabledCss};
`;

const DropdownLabel = styled.span<{ fontSize: number }>`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: bold;
    font-size: ${props => props.fontSize + 'px'};
    line-height: ${props => props.fontSize + 'px'};
    text-align: center;
    ${DgNonSelectableLabelCss}
    color: ${homeGray};
`;

interface CardletDropdownLabelProps {
    fontSize: number;
    label: string;
    dropdownOpen: boolean;
    onClick: () => void;
    disabled?: boolean;
}

export const CardletDropdownLabel = (props: CardletDropdownLabelProps): JSX.Element => {
    const dgIcons = useDgIcons();

    return (
        <DropdownButtonContainer key='dropdown_label' disabled={props.disabled} onClick={props.onClick}>
            <DropdownLabel fontSize={props.fontSize}>{props.label}</DropdownLabel>
            {dgIcons.icon(props.dropdownOpen ? dgIconNames.UpChevron : dgIconNames.DownChevron, '', 5, props.fontSize + 2, homeGray, '')}
        </DropdownButtonContainer>
    )
}
